import { useTranslation } from "react-i18next";

import Heading1 from "@components/layout/Heading";
import Loading from "@components/shared/Loading";
import StepFooter from "./StepFooter";
import { useAdhocReport } from "@context/AdhocReportContext";
import { useReportScopes } from "@hooks/query/useReportScopes";

/** Subjects and fields from the versioned catalogue. */
const StepFields: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const {
    catalog,
    isCatalogLoading,
    isCatalogError,
    refetchCatalog,
    selectedLevels,
    toggleLevel,
    selectedFields,
    setSelectedFields,
    toggleField,
    availableFields,
    finestLevel,
    filters,
    setFilters,
    goToStep,
    isEditingColumns,
  } = useAdhocReport();

  const { data: schemes = [] } = useReportScopes();
  const hasManySchemes = schemes.length > 1;

  if (isCatalogLoading) return <Loading />;
  if (isCatalogError) {
    return (
      <div className="p-4 text-sm text-red-700">
        <p>{t("reporting.catalog_load_failed")}</p>
        <button
          type="button"
          className="btn-default mt-3"
          onClick={() => refetchCatalog()}
        >
          {t("common.refresh")}
        </button>
      </div>
    );
  }

  const availableNames = new Set(availableFields.map(field => field.name));
  const chosenAvailable = selectedFields.filter(name =>
    availableNames.has(name),
  );

  const handleContinue = () => {
    // Selections that stopped being meaningful (grain changed) are pruned
    // here, filters included, so what runs is exactly what is shown.
    setSelectedFields(chosenAvailable);
    // Against the columns kept, not everything the grain offers: a filter on
    // an unticked column would survive, show the wrong field name on the next
    // step, and quietly narrow the report.
    const kept = new Set(chosenAvailable);
    kept.add(
      selectedLevels.includes("allocation_announcements")
        ? "allocationLevel1ResourceName"
        : "level1ResourceName",
    );
    setFilters(filters.filter(filter => kept.has(filter.field)));
    goToStep(isEditingColumns ? 2 : 1);
  };

  const toggleAll = (fields: typeof availableFields) => {
    const names = fields.map(field => field.name);
    const allSelected = names.every(name => selectedFields.includes(name));
    setSelectedFields(
      allSelected
        ? selectedFields.filter(name => !names.includes(name))
        : selectedFields.concat(
            names.filter(name => !selectedFields.includes(name)),
          ),
    );
  };

  const isAnnouncements = selectedLevels.includes("allocation_announcements");
  const selectedGroups = catalog
    .filter(level => selectedLevels.includes(level.name))
    .map(level => ({
      level,
      fields: level.fields.filter(field => availableNames.has(field.name)),
    }));

  const renderFields = (fields: typeof availableFields) =>
    fields.map(field => (
      <label key={field.name} className="flex items-center gap-2 py-0.5">
        <input
          type="checkbox"
          checked={selectedFields.includes(field.name)}
          onChange={() => toggleField(field.name)}
        />
        <span>{t(field.label)}</span>
      </label>
    ));

  const renderGroupHeader = (label: string, fields: typeof availableFields) => (
    <div className="flex items-center justify-between gap-3">
      <h4 className="text-sm font-medium text-gray-700">{label}</h4>
      <button
        type="button"
        className="shrink-0 text-xs text-primary-3 underline"
        aria-label={`${
          fields.every(field => selectedFields.includes(field.name))
            ? t("common.clear_all")
            : t("common.select_all")
        } ${label}`}
        onClick={() => toggleAll(fields)}
      >
        {fields.every(field => selectedFields.includes(field.name))
          ? t("common.clear_all")
          : t("common.select_all")}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col grow">
      <div className="p-4 flex flex-col gap-4 grow">
        <div>
          <Heading1 light>{t("reporting.general_step2_title")}</Heading1>
          <div className="text-sm">{t("reporting.general_step2_subtitle")}</div>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(16rem,20rem)_minmax(0,1fr)]">
          <div className="border rounded p-4">
            <h3 className="font-semibold mb-2">
              {t("reporting.adhoc_select_subjects")}
            </h3>
            <p className="mb-2 text-xs text-gray-500">
              {t("reporting.row_represents_hint")}
            </p>
            {catalog.map(level => {
              const incompatible =
                !selectedLevels.includes(level.name) &&
                selectedLevels.some(
                  selected =>
                    catalog.find(item => item.name === selected)?.family !==
                    level.family,
                );
              return (
                <label
                  key={level.name}
                  className={`flex items-center gap-2 py-1 ${
                    incompatible ? "cursor-not-allowed text-gray-400" : ""
                  }`}
                  title={
                    incompatible
                      ? (t("reporting.subject_family_switch_hint") as string)
                      : undefined
                  }
                >
                  <input
                    type="checkbox"
                    checked={selectedLevels.includes(level.name)}
                    disabled={incompatible}
                    onChange={() => toggleLevel(level.name)}
                  />
                  <span>{t(level.label)}</span>
                </label>
              );
            })}
            {selectedLevels.length > 0 && (
              <p className="mt-2 text-xs text-gray-500">
                {t("reporting.subject_family_switch_hint")}
              </p>
            )}
            {selectedLevels.includes("allocation_announcements") && (
              <p className="text-xs text-gray-500 mt-2">
                {t("reporting.announcements_standalone_hint")}
              </p>
            )}
            {selectedLevels.includes("extraction_points") && (
              <p className="mt-2 text-xs text-gray-500">
                {t("reporting.offtake_row_hint")}
              </p>
            )}
            {finestLevel && !isAnnouncements && (
              <div className="mt-3 rounded bg-gray-50 p-2 text-xs text-gray-600">
                {t("reporting.result_row_grain", {
                  level: t(`reporting.row_grain_${finestLevel.grain}`),
                })}
              </div>
            )}
          </div>

          <div className="min-w-0 border rounded p-4">
            <h3 className="font-semibold mb-2">
              {t("reporting.adhoc_select_attributes")}
            </h3>
            {!finestLevel && (
              <p className="text-sm text-gray-500">
                {t("reporting.select_subjects_first")}
              </p>
            )}
            <div className="grid gap-4 xl:grid-cols-2">
              {selectedGroups.map(({ level, fields }) => (
                <section
                  key={level.name}
                  className="min-w-0 rounded border border-gray-200 p-3"
                >
                  {fields.length > 0 ? (
                    <>
                      {renderGroupHeader(t(level.label), fields)}
                      <div className="mt-2">{renderFields(fields)}</div>
                    </>
                  ) : (
                    <>
                      <h4 className="text-sm font-medium text-gray-700">
                        {t(level.label)}
                      </h4>
                      <p className="mt-1 text-xs text-gray-500">
                        {t("reporting.subject_not_available_at_grain", {
                          level: t(finestLevel?.label ?? ""),
                        })}
                      </p>
                    </>
                  )}
                </section>
              ))}
            </div>
            {/* Said where the column is picked, not two steps later when the
                run refuses: these are read against one scheme's water year. */}
            {hasManySchemes &&
              selectedFields.some(
                field =>
                  availableFields.find(candidate => candidate.name === field)
                    ?.periodSemantics === "accounting_period",
              ) && (
                <p className="text-xs text-gray-500 mt-2">
                  {t("reporting.water_year_columns_hint")}
                </p>
              )}
          </div>
        </div>
      </div>
      <StepFooter
        onContinue={handleContinue}
        continueDisabled={chosenAvailable.length === 0}
      />
    </div>
  );
};

export default StepFields;
